import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from 'react-query'
import { Toaster } from 'react-hot-toast'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { useAuth } from '@/lib/auth'
import Layout from '@/components/Layout'
import ProtectedRoute from '@/components/ProtectedRoute'
import ErrorBoundary from '@/components/ErrorBoundary'
import { loadFFMalmoomFont } from '@/utils/fontLoader'
import { initTracking } from '@/services/tracking'

// Pages
import ReferralGatePage from '@/pages/ReferralGatePage'
import LoginPage from '@/pages/LoginPage'
import SignupPage from '@/pages/SignupPage'
import DashboardPage from '@/pages/DashboardPage'
import LettersPage from '@/pages/LettersPage'
import LetterDetailPage from '@/pages/LetterDetailPage'
import UploadPage from '@/pages/UploadPage'
import DocsPage from '@/pages/DocsPage'
import SettingsPage from '@/pages/SettingsPage'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    },
  },
})

function App() {
  const checkAuth = useAuth((state) => state.checkAuth)
  const isLoading = useAuth((state) => state.isLoading)
  const { i18n } = useTranslation()

  useEffect(() => {
    // Initialize tracking on app start
    initTracking()
    checkAuth()
  }, [checkAuth])

  useEffect(() => {
    // Load FF Malmoom font when Arabic is selected
    if (i18n.language === 'ar') {
      loadFFMalmoomFont()
    }
  }, [i18n.language])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-olive-600"></div>
      </div>
    )
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <Routes>
            {/* Referral gate is the landing page - no layout */}
            <Route path="/" element={<ReferralGatePage />} />
            <Route path="/login" element={<LoginPage />} />

            {/* Routes with layout */}
            <Route element={<Layout />}>
              <Route path="/signup" element={<SignupPage />} />

              {/* Public routes — browsable without auth */}
              <Route path="/letters" element={<LettersPage />} />
              <Route path="/letters/:id" element={<LetterDetailPage />} />

              <Route element={<ProtectedRoute />}>
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/upload" element={<UploadPage />} />
                <Route path="/docs" element={<DocsPage />} />
                <Route path="/settings" element={<SettingsPage />} />
              </Route>
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </ErrorBoundary>

      <Toaster
        position="top-right"
        toastOptions={{
          className: 'font-sans',
          style: {
            background: '#404436',
            color: '#fff',
          },
          success: {
            iconTheme: {
              primary: '#7c8362',
              secondary: '#fff',
            },
          },
        }}
      />
    </QueryClientProvider>
  )
}

export default App
